import { useFlag } from '@unleash/proxy-client-react'
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined'
import { IconButton, Tooltip, useTheme } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useParams } from 'react-router-dom'
import { BucketMedia } from '../../../../../graphql/generated'
import useBucketDownload from '../../../hooks/useBucketDownload'
import useSelectedMedia from '../../../hooks/useSelectedMedia'
import useBucket from '../../../hooks/useBucket'
import { modalVar } from '../../../../../providers/apollo/cache'

interface Props {
  media?: BucketMedia | null
}

export default function MediaDownload({ media = null }: Readonly<Props>) {
  const { bucketId } = useParams<{ bucketId: string }>()
  const { selectedMedia } = useSelectedMedia()
  const useUpdatedAlbumActions = useFlag('useUpdatedAlbumActions')
  const { isFreemiumBucket, currentUserIsOwner } = useBucket()
  const { downloadSingleMedia, downloadMultipleMedia, downloadMultipleMediaResults, downloadSingleMediaResults } = useBucketDownload()
  const mediaIds: string[] = media ? [media.id] : Array.from(selectedMedia.keys())
  const loading = downloadMultipleMediaResults.loading || downloadSingleMediaResults.loading
  const freemiumRestricted = isFreemiumBucket && selectedMedia.size > 1
  const theme = useTheme()

  const handleClick = () => {
    if (!bucketId || !mediaIds.length) return

    if (freemiumRestricted) {
      modalVar(currentUserIsOwner ? 'upsellModal' : 'premiumFeature')
      return
    }

    if (mediaIds.length > 1) {
      downloadMultipleMedia(bucketId, mediaIds)
      return
    }

    downloadSingleMedia(bucketId, mediaIds[0])
  }

  return (
    <Tooltip title={selectedMedia.size ? 'Download Media' : 'No Media Selected'} arrow>
      <div>
        <IconButton
          disabled={!mediaIds.length}
          onClick={handleClick}
          color={freemiumRestricted ? 'groupsy700' : 'primary'}
          aria-label="Download"
          data-test="download"
          id="download-button"
          sx={{
            bgcolor: freemiumRestricted ? theme.palette.groupsyPink[50] : 'inherit',
            padding: useUpdatedAlbumActions ? '4px' : '8px',
            '&:hover': () =>
              freemiumRestricted
                ? {
                    bgcolor: theme.palette.groupsyPink[50],
                    cursor: 'default'
                  }
                : {}
          }}
        >
          {loading && <CircularProgress size={20} />}
          {!loading && <DownloadIcon />}
        </IconButton>
      </div>
    </Tooltip>
  )
}
